import { useSyncExternalStore } from 'react';
import { createRoot } from 'react-dom/client';
import { AlertCircle, CheckCircle2, Info, X } from 'lucide-react';
import { clsx } from 'clsx';
import {
  type UndoableAction,
  UNDO_WINDOW_MS,
  scheduleUndoable,
  undoPending,
  flushAllPending,
  latestUndoableKey,
} from '../../lib/undoable';

type Tone = 'success' | 'error' | 'info';

interface ToastItem {
  id: number;
  message: string;
  tone: Tone;
  action?: { label: string; onClick: () => void };
  /** Set on undo toasts so Cmd/Ctrl+Z can find and close the right one. */
  undoKey?: number;
}

interface ToastOptions {
  tone?: Tone;
  action?: ToastItem['action'];
  duration?: number;
  undoKey?: number;
}

const MAX_VISIBLE = 3;

let items: ToastItem[] = [];
let nextId = 0;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach(l => l());
const subscribe = (l: () => void) => { listeners.add(l); return () => { listeners.delete(l); }; };

let hostMounted = false;

// Same pattern as confirmDialog: its own React root, so non-component code can call it.
function ensureHost() {
  if (hostMounted || typeof document === 'undefined') return;
  hostMounted = true;
  const host = document.createElement('div');
  document.body.appendChild(host);
  createRoot(host).render(<ToastHost />);
  window.addEventListener('keydown', onUndoShortcut);
  window.addEventListener('pagehide', flushAllPending);
}

export function showToast(message: string, opts: ToastOptions = {}): number {
  ensureHost();
  const id = ++nextId;
  items = [...items.slice(-(MAX_VISIBLE - 1)), { id, message, tone: opts.tone ?? 'success', action: opts.action, undoKey: opts.undoKey }];
  emit();
  setTimeout(() => dismissToast(id), opts.duration ?? 4000);
  return id;
}

export function dismissToast(id: number) {
  if (!items.some(t => t.id === id)) return;
  items = items.filter(t => t.id !== id);
  emit();
}

/**
 * Archive / soft delete with a "Deshacer" toast. Apply the change to the UI
 * BEFORE calling this; the server write runs when the toast expires (see
 * lib/undoable.ts for why it's deferred instead of restored).
 *   undoableToast('Requerimiento eliminado', { kind: 'requirements', id, patch: null,
 *     commit: async () => { ... throw on error }, revert: () => ..., errorMessage: 'No se pudo eliminar' });
 */
export function undoableToast(
  message: string,
  { errorMessage, ...action }: Omit<UndoableAction, 'onError'> & { errorMessage: string },
) {
  const key = scheduleUndoable({
    ...action,
    onError: err => showToast(`${errorMessage}: ${errorText(err)}`, { tone: 'error', duration: 8000 }),
  });
  showToast(message, {
    undoKey: key,
    duration: UNDO_WINDOW_MS,
    action: { label: 'Deshacer', onClick: () => { undoPending(key); } },
  });
}

function errorText(err: unknown): string {
  if (err && typeof err === 'object' && 'message' in err && typeof err.message === 'string') return err.message;
  return 'error desconocido';
}

function onUndoShortcut(e: KeyboardEvent) {
  if (e.key.toLowerCase() !== 'z' || !(e.metaKey || e.ctrlKey) || e.shiftKey || e.altKey) return;
  // Leave text undo alone inside fields.
  const t = e.target as HTMLElement | null;
  if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) return;
  const key = latestUndoableKey();
  if (key === undefined || !undoPending(key)) return;
  e.preventDefault();
  const toast = items.find(i => i.undoKey === key);
  if (toast) dismissToast(toast.id);
}

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);

const TONE_ICON = {
  success: { Icon: CheckCircle2, className: 'text-emerald-400' },
  error: { Icon: AlertCircle, className: 'text-rose-400' },
  info: { Icon: Info, className: 'text-sky-400' },
} as const;

function ToastHost() {
  const list = useSyncExternalStore(subscribe, () => items);
  // The ThemeContext provider isn't above this root; read the class it puts on <html>.
  const isLight = !document.documentElement.classList.contains('dark-theme');

  return (
    <div className="fixed inset-x-0 bottom-20 sm:bottom-6 z-[200] flex flex-col items-center gap-2 px-4 pointer-events-none">
      {list.map(t => {
        const { Icon, className: iconClass } = TONE_ICON[t.tone];
        return (
          <div
            key={t.id}
            role={t.tone === 'error' ? 'alert' : 'status'}
            className={clsx(
              'pointer-events-auto animate-in fade-in slide-in-from-bottom-2 duration-200 flex items-center gap-3 w-full sm:w-auto sm:min-w-[320px] max-w-[480px] pl-3 pr-1.5 py-1.5 rounded-xl border shadow-xl text-sm',
              isLight ? 'bg-slate-900 border-slate-800 text-white' : 'bg-slate-800 border-white/10 text-slate-100'
            )}
          >
            <span className="w-4 h-4 shrink-0 flex items-center justify-center">
              <Icon size={16} className={iconClass} />
            </span>
            <span className="flex-1 min-w-0 truncate font-medium py-1">{t.message}</span>
            {t.action && (
              <button
                onClick={() => { t.action!.onClick(); dismissToast(t.id); }}
                className="shrink-0 flex items-center gap-2 px-2.5 py-1 rounded-lg font-bold text-indigo-300 hover:bg-white/10 transition-colors"
              >
                {t.action.label}
                {t.undoKey !== undefined && (
                  <kbd className="hidden md:inline text-[11px] font-semibold text-slate-400">{isMac ? '⌘Z' : 'Ctrl+Z'}</kbd>
                )}
              </button>
            )}
            <button
              onClick={() => dismissToast(t.id)}
              aria-label="Cerrar"
              className="shrink-0 w-7 h-7 flex items-center justify-center rounded-lg text-slate-400 hover:text-white hover:bg-white/10 transition-colors"
            >
              <X size={14} />
            </button>
          </div>
        );
      })}
    </div>
  );
}
